import { useMemo } from "react";

import { resolveLandingPath } from "@/app/auth/resolveNavAccess";
import { useNavContext } from "@/app/auth/useNavContext";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import useTranslations from "@/shared/lang/useTranslations";
import { hasStepUpMethod } from "@/shared/utils/stepUp";

import type { VerificationWelcomeShortcut } from "../components/VerificationWelcome";
import { resolveVerificationWelcomeShortcuts } from "../utils/verificationWelcome";

export interface VerificationWelcomeShortcuts {
  /** The choices, without the landing page: the welcome's primary button already leads there. */
  shortcuts: VerificationWelcomeShortcut[];
  /** Where the primary "go home" button leads, or `null` when the user can see nothing at all. */
  landingPath: string | null;
}

/**
 * The welcome's shortcuts for the signed-in user, translated: the sidebar's own label as the title (one name
 * per destination, everywhere) and a line on what the user can do there.
 */
export function useVerificationWelcomeShortcuts(): VerificationWelcomeShortcuts {
  const { user } = useAuth();
  const navContext = useNavContext();
  const { t } = useTranslations("verification");
  const { t: tGlobal } = useTranslations("global");
  // Offered only while the member has no way to confirm money movements yet — the authenticator OR a PIN.
  const twoFactorEnabled = hasStepUpMethod(user);

  return useMemo(() => {
    const landingPath = navContext ? resolveLandingPath(navContext) : null;
    const specs = resolveVerificationWelcomeShortcuts(navContext, {
      twoFactorEnabled,
      excludeHref: landingPath,
    });
    return {
      landingPath,
      shortcuts: specs.map((spec) => ({
        id: spec.id,
        href: spec.href,
        title: spec.labelKey ? tGlobal(spec.labelKey) : t("welcome_shortcut_security_title"),
        description: t(`welcome_shortcut_${spec.id}_description`),
      })),
    };
  }, [navContext, twoFactorEnabled, t, tGlobal]);
}
